import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Box,
  Alert
} from '@mui/material';

interface CreateProgramModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (program: any) => void;
}

const CreateProgramModal: React.FC<CreateProgramModalProps> = ({ open, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: 'Aceleración' as 'Aceleración' | 'Incubación' | 'Capacitación',
    startDate: '',
    endDate: ''
  });
  const [error, setError] = useState('');

  const handleChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [field]: e.target.value });
    setError('');
  };

  const handleSubmit = () => {
    // Validaciones
    if (!formData.name.trim()) {
      setError('El nombre del programa es obligatorio');
      return;
    }
    if (!formData.description.trim()) {
      setError('La descripción es obligatoria');
      return;
    }
    if (!formData.startDate || !formData.endDate) {
      setError('Las fechas de inicio y fin son obligatorias');
      return;
    }
    if (new Date(formData.startDate) >= new Date(formData.endDate)) {
      setError('La fecha de fin debe ser posterior a la fecha de inicio');
      return;
    }

    // Crear programa
    const newProgram = {
      id: `program_${Date.now()}`,
      ...formData,
      status: 'upcoming' as const,
      cohortIds: [],
      adminId: 'user_admin_1',
      createdAt: new Date().toISOString()
    };

    onSubmit(newProgram);
    handleClose();
  };

  const handleClose = () => {
    setFormData({
      name: '',
      description: '',
      type: 'Aceleración',
      startDate: '',
      endDate: ''
    });
    setError('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Crear Nuevo Programa</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
          {error && <Alert severity="error">{error}</Alert>}
          
          <TextField
            label="Nombre del Programa"
            value={formData.name}
            onChange={handleChange('name')}
            fullWidth
            required
            placeholder="Ej: Programa de Aceleración Fintech 2025"
          />

          <TextField
            label="Descripción"
            value={formData.description}
            onChange={handleChange('description')}
            fullWidth
            required
            multiline
            rows={3}
            placeholder="Describe los objetivos y alcance del programa"
          />

          <TextField
            select
            label="Tipo de Programa"
            value={formData.type}
            onChange={handleChange('type')}
            fullWidth
            required
          >
            <MenuItem value="Aceleración">Aceleración</MenuItem>
            <MenuItem value="Incubación">Incubación</MenuItem>
            <MenuItem value="Capacitación">Capacitación</MenuItem>
          </TextField>

          <TextField
            label="Fecha de Inicio"
            type="date"
            value={formData.startDate}
            onChange={handleChange('startDate')}
            fullWidth
            required
            InputLabelProps={{ shrink: true }}
          />

          <TextField
            label="Fecha de Fin"
            type="date"
            value={formData.endDate}
            onChange={handleChange('endDate')}
            fullWidth
            required
            InputLabelProps={{ shrink: true }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancelar</Button>
        <Button onClick={handleSubmit} variant="contained">
          Crear Programa
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateProgramModal;
